import type {
  AddressDto,
  CreateAddressDto,
  OrganizationDto,
  OrganizationExtendedDto,
  UpdateAddressDto,
  UserDto,
  UserRoleBindingDto,
  UserRoleDto,
  UserUpdateDto,
} from '@/castapi';

import { PERMISSIONS, USER_GROUP_IDS, USER_ROLE_IDS } from '@/utils';
import { uploadImage } from '@/store/modules/common/files';
import { omit } from '@/utils/helpers/data';
import { AppLogger } from '@/logger';
import {
  getAddressApi,
  getAwsApi,
  getErrorMessage,
  getOrdersApi,
  getOrganizationsApi,
  getUsersApi,
  omitUserSecretFields,
} from '@/castapi/helpers';
import type { IActionParams, RootState } from '@/store/modules/index';

export interface RoleWithBindingDto extends UserRoleDto, UserRoleBindingDto {}

const logger = new AppLogger('organizations state');

const filterUniqueUsers = (user: UserDto, index: number, self: UserDto[]) =>
  self.findIndex(value => value.userId === user.userId) >= index;

interface IOrganizationState {
  organizations: OrganizationDto[];
  canCreateOrganization?: boolean;
  addresses: AddressDto[];
  userRoleBinding: UserRoleBindingDto[];
  users: UserDto[];
  addressChanging: boolean;
  changeAddressError?: string;
  organizationChanging: boolean;
  changeOrganizationError?: string;
  invitationCreating: boolean;
  invitationCreateError?: string;
  organizationTransferring: boolean;
  organizationTransferError?: string;
  userDeleting?: UserDto;
  userDeleteError?: string;
  userUpdating?: UserDto;
  userUpdateError?: string;
  userRolesChanging?: UserDto;
  userRolesChangeError?: string;
  isLoading: boolean;
  error: string;
  isTrialExists: { exists: boolean; expired: boolean; upgraded: boolean };
  isPurchaseAvailableForOrganization: boolean;
  isSupportAvailableForOrganization: boolean;
}

const initialState = (): IOrganizationState => ({
  organizations: [],
  addresses: [],
  userRoleBinding: [],
  users: [],
  addressChanging: false,
  changeAddressError: undefined,
  organizationChanging: false,
  changeOrganizationError: undefined,
  invitationCreating: false,
  invitationCreateError: undefined,
  organizationTransferring: false,
  organizationTransferError: undefined,
  userDeleting: undefined,
  userDeleteError: undefined,
  userUpdating: undefined,
  userUpdateError: undefined,
  userRolesChanging: undefined,
  userRolesChangeError: undefined,
  canCreateOrganization: undefined,
  isLoading: false,
  error: undefined,
  isTrialExists: { exists: false, expired: false, upgraded: false },
  isPurchaseAvailableForOrganization: true,
  isSupportAvailableForOrganization: true,
});

interface Getters {
  organization: OrganizationDto;
  addresses: AddressDto[];
  organizationId: number;
  organizationUserRoles: UserRoleBindingDto[];
  organizationUsers: UserDto[];
  userRolesGetter: (userId: number) => RoleWithBindingDto[];
  activeUserMainRole: (userId: number) => RoleWithBindingDto;
  activeUserRoles: (userId: number) => RoleWithBindingDto[];
  userRolesBindingGetter: (userId: number) => UserRoleBindingDto[];
  isPurchaseAvailableForOrganization: boolean;
  isSupportAvailableForOrganization: boolean;
}

type ActionParams = IActionParams<IOrganizationState, Getters>;

export default {
  namespaced: true,
  state: initialState,
  mutations: {
    RESET_STATE(state: IOrganizationState): void {
      Object.assign(state, initialState());
    },
    ORGANIZATIONS_LOADED(state: IOrganizationState, organizationsExtended: OrganizationExtendedDto[]): void {
      state.organizations = [];
      state.addresses = [];
      state.userRoleBinding = [];
      state.users = [];
      organizationsExtended.forEach(org => {
        state.organizations = [...state.organizations, org.organization];
        state.addresses = [...state.addresses, ...org.addresses];
        state.userRoleBinding = [...state.userRoleBinding, ...org.rolesBinding];
        state.users = [...state.users, ...org.users];
      });
    },
    CAN_CREATE_ORGANIZATION_LOADED(state: IOrganizationState, canCreateOrganization: boolean): void {
      state.canCreateOrganization = canCreateOrganization;
    },
    ORGANIZATION_ADDRESSES_LOADED(
      state: IOrganizationState,
      { organizationId, addresses }: { organizationId: number; addresses: AddressDto[] },
    ): void {
      state.addresses = [
        ...state.addresses.filter(address => address.organizationRef !== organizationId),
        ...addresses,
      ];
    },
    ADDRESS_CHANGING(state: IOrganizationState): void {
      state.changeAddressError = undefined;
      state.addressChanging = true;
    },
    CHANGE_ADDRESS_ERROR(state: IOrganizationState, payload: Error): void {
      state.changeAddressError = getErrorMessage(payload);
      state.addressChanging = false;
    },
    ADDRESS_CHANGED(state: IOrganizationState): void {
      state.addressChanging = false;
      state.changeAddressError = undefined;
    },
    ORGANIZATION_CHANGING(state: IOrganizationState): void {
      state.changeOrganizationError = undefined;
      state.organizationChanging = true;
    },
    CHANGE_ORGANIZATION_ERROR(state: IOrganizationState, payload: Error): void {
      state.changeOrganizationError = getErrorMessage(payload);
      state.organizationChanging = false;
    },
    ORGANIZATION_CHANGED(state: IOrganizationState, newOrganization: OrganizationDto): void {
      state.organizationChanging = false;
      state.organizations = state.organizations.map(org =>
        org.organizationId === newOrganization.organizationId ? newOrganization : org,
      );
    },
    INVITATION_CREATING(state: IOrganizationState): void {
      state.invitationCreating = true;
      state.invitationCreateError = undefined;
    },
    INVITATION_CREATED(state: IOrganizationState): void {
      state.invitationCreating = false;
      state.invitationCreateError = undefined;
    },
    INVITATION_CANCELED(state: IOrganizationState): void {
      state.invitationCreating = false;
      state.invitationCreateError = undefined;
    },
    INVITATION_ERROR(state: IOrganizationState, payload: Error): void {
      state.invitationCreating = false;
      state.invitationCreateError = getErrorMessage(payload);
    },
    ORGANIZATION_TRANSFERRING(state: IOrganizationState): void {
      state.organizationTransferring = true;
      state.organizationTransferError = undefined;
    },
    ORGANIZATION_TRANSFERRED(state: IOrganizationState): void {
      state.organizationTransferring = false;
      state.organizationTransferError = undefined;
    },
    ORGANIZATION_TRANSFER_ERROR(state: IOrganizationState, payload: Error): void {
      state.organizationTransferring = false;
      state.organizationTransferError = getErrorMessage(payload);
    },
    USER_DELETING(state: IOrganizationState, user: UserDto): void {
      state.userDeleteError = undefined;
      state.userDeleting = user;
    },
    USER_DELETED(state: IOrganizationState): void {
      state.userDeleting = undefined;
      state.userDeleteError = undefined;
    },
    USER_DELETE_ERROR(state: IOrganizationState, payload: Error): void {
      state.userDeleting = undefined;
      state.userDeleteError = getErrorMessage(payload);
    },
    USER_ROLES_CHANGING(state: IOrganizationState, user: UserDto): void {
      state.userRolesChanging = user;
      state.userRolesChangeError = undefined;
    },
    USER_ROLES_CHANGED(state: IOrganizationState): void {
      state.userRolesChanging = undefined;
      state.userRolesChangeError = undefined;
    },
    USER_ROLES_CHANGE_ERROR(state: IOrganizationState, payload: Error): void {
      state.userRolesChanging = undefined;
      state.userRolesChangeError = getErrorMessage(payload);
    },
    USER_UPDATING(state: IOrganizationState, user: UserDto): void {
      state.userUpdating = user;
      state.userUpdateError = undefined;
    },
    USER_UPDATED(state: IOrganizationState): void {
      state.userUpdating = undefined;
      state.userUpdateError = undefined;
    },
    USER_UPDATE_STOPPED(state: IOrganizationState): void {
      state.userUpdating = undefined;
      state.userUpdateError = undefined;
    },
    USER_UPDATE_ERROR(state: IOrganizationState, payload: Error): void {
      state.userUpdateError = getErrorMessage(payload);
      state.userUpdating = undefined;
    },
    RESET_ADDRESS_ERROR(state: IOrganizationState): void {
      state.changeAddressError = undefined;
    },
    RESET_UPDATE_ORGANIZATION_ERROR(state: IOrganizationState): void {
      state.changeOrganizationError = undefined;
    },
    RESET_UPDATE_USER_ERROR(state: IOrganizationState): void {
      state.userUpdateError = undefined;
    },
    LOADING(state: IOrganizationState): void {
      state.isLoading = true;
    },
    LOADED(state: IOrganizationState): void {
      // unused
      state.isLoading = false;
    },
    ERROR(state: IOrganizationState, error: string): void {
      state.isLoading = false;
      state.error = error;
    },
    IS_TRIAL_EXISTS_LOADED(
      state: IOrganizationState,
      isTrialExists: { exists: boolean; expired: boolean; upgraded: boolean },
    ): void {
      state.isLoading = false;
      state.isTrialExists = isTrialExists;
    },
    SET_IS_PURCHASE_AVAILABLE_FOR_ORGANIZATION(
      state: IOrganizationState,
      isPurchaseAvailableForOrganization: boolean,
    ): void {
      state.isLoading = false;
      state.isPurchaseAvailableForOrganization = isPurchaseAvailableForOrganization;
    },
    SET_IS_SUPPORT_AVAILABLE_FOR_ORGANIZATION(
      state: IOrganizationState,
      isSupportAvailableForOrganization: boolean,
    ): void {
      state.isLoading = false;
      state.isSupportAvailableForOrganization = isSupportAvailableForOrganization;
    },
  },
  actions: {
    async getUserOrganizations({ dispatch, commit, rootGetters }: ActionParams, token: string): Promise<void> {
      try {
        const response = await getOrganizationsApi(
          token || rootGetters['login/accessToken'],
        ).organizationsControllerGetUserOrganizations();
        commit('ORGANIZATIONS_LOADED', response.data);
        if (!response.data.length) {
          await dispatch('getCanCreateOrganization', token);
        }
      } catch (error) {
        logger.captureStoreError('getUserOrganizations', error);
        // Plan: handle error
      }
    },
    async getCanCreateOrganization({ commit, rootGetters }: ActionParams, token: string): Promise<void> {
      try {
        const response = await getUsersApi(
          token || rootGetters['login/accessToken'],
        ).usersControllerGetCanCreateOrganization();
        commit('CAN_CREATE_ORGANIZATION_LOADED', response.data);
      } catch (error) {
        logger.captureStoreError('getCanCreateOrganization', error);
      }
    },
    async getOrganizationAddresses({ commit, rootGetters }: ActionParams, organizationId: number): Promise<void> {
      try {
        const response = await getAddressApi(
          rootGetters['login/accessToken'],
        ).addressControllerGetOrganizationAddresses(organizationId);
        commit('ORGANIZATION_ADDRESSES_LOADED', { organizationId, addresses: response.data });
      } catch (error) {
        logger.captureStoreError('getOrganizationAddresses', error, { organizationId });
        // Plan: handle error
      }
    },
    async addAddress({ commit, dispatch, rootGetters }: ActionParams, address: CreateAddressDto): Promise<void> {
      commit('ADDRESS_CHANGING');
      try {
        await getAddressApi(rootGetters['login/accessToken']).addressControllerCreateAddress(address);
        if (rootGetters['login/isAdmin']) {
          await dispatch('adminOrganizations/getOrganizationData', address.organizationRef, { root: true });
        } else {
          await dispatch('getOrganizationAddresses', address.organizationRef);
        }
        commit('ADDRESS_CHANGED', 'added');
      } catch (error) {
        commit('CHANGE_ADDRESS_ERROR', error);
        logger.captureStoreError('addAddress', error, { address });
      }
    },
    async updateAddress({ commit, dispatch, rootGetters }: ActionParams, address: UpdateAddressDto): Promise<void> {
      commit('ADDRESS_CHANGING', address);
      try {
        await getAddressApi(rootGetters['login/accessToken']).addressControllerUpdateAddress(address);
        if (rootGetters['login/isAdmin']) {
          await dispatch('adminOrganizations/getOrganizationData', address.organizationRef, { root: true });
        } else {
          await dispatch('getOrganizationAddresses', address.organizationRef);
        }
        commit('ADDRESS_CHANGED', 'updated');
      } catch (error) {
        commit('CHANGE_ADDRESS_ERROR', error);
        logger.captureStoreError('updateAddress', error, { address });
      }
    },
    async deleteAddress({ commit, dispatch, rootGetters }: ActionParams, address: AddressDto): Promise<void> {
      commit('ADDRESS_CHANGING');
      try {
        await getAddressApi(rootGetters['login/accessToken']).addressControllerDeleteAddress({
          addressId: address.addressId,
        });
        if (rootGetters['login/isAdmin']) {
          await dispatch('adminOrganizations/getOrganizationData', address.organizationRef, { root: true });
        } else {
          await dispatch('getOrganizationAddresses', address.organizationRef);
        }
        commit('ADDRESS_CHANGED', 'deleted');
      } catch (error) {
        commit('CHANGE_ADDRESS_ERROR', error);
        logger.captureStoreError('deleteAddress', error, { address });
      }
    },
    async updateOrganizationData(
      { commit, rootState }: ActionParams,
      { organization, avatarImage }: { organization: OrganizationDto; avatarImage?: string },
    ): Promise<void> {
      commit('ORGANIZATION_CHANGING');
      try {
        let avatarUrl: undefined | string;
        const accessToken = rootState.login.accessToken;
        if (avatarImage) {
          avatarUrl = await uploadImage(
            avatarImage,
            `organization-avatars/${organization.organizationId}`,
            getAwsApi(accessToken),
          );
        }

        let updateBody = { ...organization };
        if (avatarImage && avatarUrl) {
          updateBody = { ...updateBody, organizationAvatar: avatarUrl };
        }

        const response = await getOrganizationsApi(accessToken).organizationsControllerUpdateOrganization(updateBody);
        commit('ORGANIZATION_CHANGED', response.data);
      } catch (error) {
        commit('CHANGE_ORGANIZATION_ERROR', error);
        logger.captureStoreError('updateOrganizationData', error, { organization, avatarImage });
      }
    },
    async inviteMember(
      { commit, dispatch, rootState }: ActionParams,
      {
        user,
        organization,
        roles,
      }: {
        user: UserDto;
        organization: OrganizationDto;
        roles: string[];
      },
    ): Promise<void> {
      commit('INVITATION_CREATING');
      try {
        await getUsersApi(rootState.login.accessToken).usersControllerInviteMember({
          user,
          organization,
          roles,
        });
        commit('INVITATION_CREATED');
        await dispatch('getUserOrganizations', rootState.login.accessToken);
      } catch (error) {
        commit('INVITATION_ERROR', error);
        logger.captureStoreError('inviteMember', error, {
          invitedUser: omitUserSecretFields(user),
          organization,
          roles,
        });
      }
    },
    async transferOrganization(
      { commit, dispatch, rootState }: ActionParams,
      { userId, organizationId, notes }: { userId: number; organizationId: number; notes: string },
    ): Promise<void> {
      commit('ORGANIZATION_TRANSFERRING');
      try {
        await getUsersApi(rootState.login.accessToken).usersControllerTransferOrganization({
          userId,
          organizationId,
          notes,
        });
        commit('ORGANIZATION_TRANSFERRED');
        await dispatch('getUserOrganizations', rootState.login.accessToken);
        await dispatch('adminOrganizations/getOrganizationData', organizationId, { root: true });
      } catch (error) {
        commit('ORGANIZATION_TRANSFER_ERROR', error);
        logger.captureStoreError('transferOrganization', error, { userId, organizationId, notes });
      }
    },
    async resendInvitationToMember(
      { commit, rootState }: ActionParams,
      {
        user,
        organization,
      }: {
        user: UserDto;
        organization: OrganizationDto;
      },
    ): Promise<void> {
      commit('INVITATION_CREATING');
      try {
        await getUsersApi(rootState.login.accessToken).usersControllerResendMemberInvitation({ user, organization });
        commit('INVITATION_CREATED');
      } catch (error) {
        commit('INVITATION_ERROR', error);
        logger.captureStoreError('resendInvitationToMember', error, {
          invitedUser: omitUserSecretFields(user),
          organization,
        });
      }
    },
    async cancelMemberInvitation(
      { commit, rootState }: ActionParams,
      {
        user,
        organization,
      }: {
        user: UserDto;
        organization: OrganizationDto;
      },
    ): Promise<void> {
      commit('INVITATION_CREATING');
      try {
        await getUsersApi(rootState.login.accessToken).usersControllerCancelUserInvitation({
          userId: user.userId,
          organizationId: organization.organizationId,
        });
        commit('INVITATION_CANCELED');
      } catch (error) {
        commit('INVITATION_ERROR', error);
        logger.captureStoreError('cancelMemberInvitation', error, {
          invitedUser: omitUserSecretFields(user),
          organization,
        });
      }
    },
    async removeUserFromOrganization(
      { commit, rootState }: ActionParams,
      {
        user,
        organization,
      }: {
        user: UserDto;
        organization: OrganizationDto;
      },
    ): Promise<void> {
      commit('USER_DELETING', user);
      try {
        await getUsersApi(rootState.login.accessToken).usersControllerRemoveUserFromOrganization({
          userId: user.userId,
          organizationId: organization.organizationId,
        });
        commit('USER_DELETED');
      } catch (error) {
        commit('USER_DELETE_ERROR', error);
        logger.captureStoreError('removeUserFromOrganization', error, {
          removingUser: omitUserSecretFields(user),
          organization,
        });
      }
    },
    async changeUserRoles(
      { commit, dispatch, rootState, getters }: ActionParams,
      {
        user,
        roles,
      }: {
        user: UserDto;
        roles: string[];
      },
    ): Promise<void> {
      commit('USER_ROLES_CHANGING', user);
      const organizationId = getters.organization.organizationId;
      try {
        await getUsersApi(rootState.login.accessToken).usersControllerChangeUserRoles({
          organizationId,
          roles,
          userId: user.userId,
        });
        commit('USER_ROLES_CHANGED');
        await dispatch('getUserOrganizations', rootState.login.accessToken);
      } catch (error) {
        commit('USER_ROLES_CHANGE_ERROR', error);
        logger.captureStoreError('changeUserRoles', error, {
          changingUser: omitUserSecretFields(user),
          roles,
        });
      }
    },
    async updateUser(
      { commit, dispatch, getters, rootGetters }: ActionParams,
      { user, avatarImage }: { user: UserDto; avatarImage?: string },
    ): Promise<void> {
      commit('USER_UPDATING', user);
      try {
        let avatar: undefined | string;
        const accessToken = rootGetters['login/accessToken'];
        if (avatarImage) {
          avatar = await uploadImage(avatarImage, `user-avatars/${user.userId}`, getAwsApi(accessToken));
        }

        let updateBody = omit(
          user,
          Object.keys(user).filter(f => !['firstName', 'lastName', 'phoneNumber', 'email', 'avatar'].includes(f)),
        ) as UserUpdateDto;
        if (avatarImage && avatar) {
          updateBody = { ...updateBody, avatar };
        }

        const response = await getUsersApi(accessToken).usersControllerUpdate(
          user.userId,
          getters.organizationId,
          updateBody,
        );
        if (rootGetters['login/user'].userId === user.userId) {
          await dispatch('login/updateUser', response.data, { root: true });
        } else {
          await dispatch('getUserOrganizations', accessToken);
        }
        commit('USER_UPDATED');
      } catch (error) {
        if (getErrorMessage(error) === 'You have reached the limit of phone number changes for today') {
          commit('login/PHONE_VERIFICATION_LIMIT_REACHED', null, { root: true });
          commit('USER_UPDATE_STOPPED');
          return;
        }
        commit('USER_UPDATE_ERROR', error);
        logger.captureStoreError('updateUser', error, { changingUser: omitUserSecretFields(user), avatarImage });
      }
    },
    async createOrganization(
      { commit, dispatch, rootGetters }: ActionParams,
      { organizationName, countryRef }: { organizationName: string; countryRef: number },
    ): Promise<void> {
      commit('ORGANIZATION_CHANGING');
      try {
        const accessToken = rootGetters['login/accessToken'];
        const userId = rootGetters['login/userId'];
        await getOrganizationsApi(accessToken).organizationsControllerCreateOrganization({
          userId,
          organizationName,
          countryRef,
        });
        commit('ORGANIZATION_CHANGED');
        await dispatch('login/GET_CURRENT_USER_DATA', accessToken, { root: true });
      } catch (error) {
        commit('CHANGE_ORGANIZATION_ERROR', error);
        logger.captureStoreError('createOrganization', error, { organizationName, countryRef });
      }
    },
    async isTrialExistsInOrganization({ commit, getters, rootGetters }: ActionParams) {
      commit('LOADING');
      try {
        const accessToken = rootGetters['login/accessToken'];
        const organizationId = getters.organizationId;
        const response = await getOrdersApi(accessToken).ordersControllerIsTrialExistsInOrganization(organizationId);
        commit('IS_TRIAL_EXISTS_LOADED', response.data);
      } catch (error) {
        commit('ERROR', error);
        logger.captureStoreError('isTrialExistsInOrganization', error);
      }
    },
    async getPurchaseAvailabilityForOrganization({ commit, getters, rootGetters }: ActionParams): Promise<void> {
      try {
        const accessToken = rootGetters['login/accessToken'];
        const organizationId = getters.organizationId;
        if (organizationId) {
          commit('LOADING');
          const result =
            await getOrdersApi(accessToken).ordersControllerIsPurchaseAvailableForOrganization(organizationId);
          commit('SET_IS_PURCHASE_AVAILABLE_FOR_ORGANIZATION', result.data);
        }
      } catch (error) {
        commit('ERROR', error);
        logger.captureStoreError('getPurchaseAvailabilityForOrganization', error);
      }
    },
    async getSupportAvailabilityForOrganization({ commit, getters, rootGetters }: ActionParams): Promise<void> {
      try {
        const accessToken = rootGetters['login/accessToken'];
        const organizationId = getters.organizationId;
        if (organizationId) {
          commit('LOADING');
          const result =
            await getOrganizationsApi(accessToken).organizationsControllerIsSupportAvailableForOrganization(
              organizationId,
            );
          commit('SET_IS_SUPPORT_AVAILABLE_FOR_ORGANIZATION', result.data);
        }
      } catch (error) {
        commit('ERROR', error);
        logger.captureStoreError('getSupportAvailabilityForOrganization', error);
      }
    },
    resetState({ commit }: ActionParams): void {
      commit('RESET_STATE');
    },
  },
  getters: {
    addresses: (state: IOrganizationState): AddressDto[] => state.addresses,
    shippingAddresses: (state: IOrganizationState): AddressDto[] =>
      state.addresses.filter(address => !address.isBilling),
    billingAddresses: (state: IOrganizationState): AddressDto[] => state.addresses.filter(address => address.isBilling),
    addressChanging: (state: IOrganizationState): boolean => state.addressChanging,
    changeAddressError: (state: IOrganizationState): undefined | string => state.changeAddressError,
    organization: (state: IOrganizationState): undefined | OrganizationDto =>
      state.organizations.length ? state.organizations[0] : undefined,
    organizationId: (_state: IOrganizationState, getters: Getters): number => getters.organization?.organizationId,
    organizationName: (_state: IOrganizationState, getters: Getters): string => getters.organization?.organizationName, // unused
    organizationAddresses: (_state: IOrganizationState, getters: Getters): AddressDto[] =>
      getters.organization
        ? getters.addresses.filter((address: AddressDto) => address.organizationRef === getters.organizationId)
        : [],
    organizationChanging: (state: IOrganizationState): boolean => state.organizationChanging,
    changeOrganizationError: (state: IOrganizationState): undefined | string => state.changeOrganizationError,
    organizationUserRoles: (state: IOrganizationState, getters: Getters): UserRoleBindingDto[] =>
      state.userRoleBinding.filter(roleBinding => roleBinding.organizationRef === getters.organizationId),
    organizationUsers: (state: IOrganizationState, getters: Getters): UserDto[] =>
      state.users
        .filter(user => getters.organizationUserRoles.find((role: UserRoleBindingDto) => role.userRef === user.userId))
        .filter(filterUniqueUsers),
    organizationActiveUsers: (_state: IOrganizationState, getters: Getters): UserDto[] =>
      getters.organizationUsers.filter((user: UserDto) => user.active),
    userRolesBindingGetter:
      (_state: IOrganizationState, getters: Getters) =>
      (userId: number): UserRoleBindingDto[] =>
        getters.organizationUserRoles.filter((roleBinding: UserRoleBindingDto) => roleBinding.userRef === userId),
    userRoleBinding: (state: IOrganizationState): UserRoleBindingDto[] => state.userRoleBinding,
    invitationCreating: (state: IOrganizationState): boolean => state.invitationCreating,
    invitationCreateError: (state: IOrganizationState): undefined | string => state.invitationCreateError,
    organizationTransferring: (state: IOrganizationState): boolean => state.organizationTransferring,
    organizationTransferError: (state: IOrganizationState): undefined | string => state.organizationTransferError,
    userDeleteError: (state: IOrganizationState): undefined | string => state.userDeleteError,
    userRolesGetter:
      (
        _state: IOrganizationState,
        getters: Getters,
        _rootState: RootState,
        rootGetters: {
          'dictionary/roleTypeGetter': (roleId: number) => UserRoleDto;
        },
      ) =>
      (userId: number): RoleWithBindingDto[] =>
        getters.userRolesBindingGetter(userId).map((binding: UserRoleBindingDto) => ({
          ...rootGetters['dictionary/roleTypeGetter'](binding.userRoleRef),
          ...binding,
        })),
    userMainRole:
      (_state: IOrganizationState, getters: Getters) =>
      (userId: number): undefined | RoleWithBindingDto =>
        getters
          .userRolesGetter(userId)
          .reduce(
            (a: undefined | RoleWithBindingDto, c: RoleWithBindingDto) => (a && a.priority > c.priority ? a : c),
            undefined,
          ),
    activeUserRoles:
      (_state: IOrganizationState, getters: Getters) =>
      (userId: number): RoleWithBindingDto[] =>
        getters.userRolesGetter(userId).filter((r: RoleWithBindingDto) => r.isActive),
    activeUserMainRole:
      (_state: IOrganizationState, getters: Getters) =>
      (userId: number): undefined | RoleWithBindingDto =>
        getters
          .userRolesGetter(userId)
          .filter(r => r.isActive)
          .reduce(
            (a: undefined | RoleWithBindingDto, c: RoleWithBindingDto) => (a && a.priority > c.priority ? a : c),
            undefined,
          ),
    activeUserMainRoleWithPermissions:
      (_state: IOrganizationState, getters: Getters): undefined | ((userId: number) => RoleWithBindingDto) =>
      (userId: number) => ({
        ...getters.activeUserMainRole(userId),
        ...getters.userRolesGetter(userId).reduce((result: Record<string, boolean>, role: RoleWithBindingDto) => {
          return Object.values(PERMISSIONS).reduce((obj, key) => ({ ...obj, [key]: result[key] || role[key] }), {});
        }, {}),
      }),
    isAdmin:
      (_state: IOrganizationState, getters: Getters) =>
      (userId: number): boolean =>
        getters.activeUserMainRole(userId)?.userGroupRef === USER_GROUP_IDS.ADMIN,
    isOwner:
      (_state: IOrganizationState, getters: Getters) =>
      (userId: number): boolean =>
        getters.activeUserMainRole(userId)?.userRoleId === USER_ROLE_IDS.OWNER_USER_ROLE_ID,
    isDemo:
      (_state: IOrganizationState, getters: Getters) =>
      (userId: number): boolean =>
        getters.activeUserMainRole(userId)?.userRoleId === USER_ROLE_IDS.DEMO_USER_ROLE_ID,
    isMember:
      (_state: IOrganizationState, getters: Getters) =>
      (userId: number): boolean =>
        getters.activeUserMainRole(userId)?.userRoleId === USER_ROLE_IDS.MEMBER_USER_ROLE_ID,
    isEmptyRole:
      (_state: IOrganizationState, getters: Getters) =>
      (userId: number): boolean =>
        !getters.activeUserRoles(userId).length,
    roleId:
      (_state: IOrganizationState, getters: Getters) =>
      (userId: number): undefined | number =>
        getters.activeUserMainRole(userId)?.userRoleId,
    roleName:
      (_state: IOrganizationState, getters: Getters) =>
      (userId: number): undefined | string =>
        getters.activeUserMainRole(userId)?.userRoleName?.toLowerCase(),
    userRolesChanging: (state: IOrganizationState): undefined | UserDto => state.userRolesChanging,
    userRolesChangeError: (state: IOrganizationState): undefined | string => state.userRolesChangeError,
    userUpdating: (state: IOrganizationState): undefined | UserDto => state.userUpdating,
    userUpdateError: (state: IOrganizationState): undefined | string => state.userUpdateError,
    canCreateOrganization: (state: IOrganizationState): undefined | boolean => state.canCreateOrganization,
    trialExists: (state: IOrganizationState): boolean => state.isTrialExists.exists,
    trialExpired: (state: IOrganizationState): boolean => state.isTrialExists.expired,
    trialUpgraded: (state: IOrganizationState): boolean => state.isTrialExists.upgraded,
    isPurchaseAvailableForOrganization: (state: IOrganizationState, _getters, _rootState, rootGetters): boolean =>
      rootGetters['login/isEducational'] ? state.isPurchaseAvailableForOrganization : true,
    isSupportAvailableForOrganization: (state: IOrganizationState, _getters, _rootState, rootGetters): boolean =>
      rootGetters['login/isEducational'] ? state.isSupportAvailableForOrganization : true,
  },
};
